import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { companiesService } from '@/services/companiesService'
import { useAuth } from '@/contexts/AuthContext'
import { getUserCompanyIds } from '@/utils/costCenter'

export interface CompanyOption {
  id: number
  name: string
}

/**
 * Empresas que o usuário pode selecionar — o par por EMPRESA de
 * `useAvailableCostCenters`, para as telas cujo eixo saiu do centro de custo
 * (lote 4 da limpeza das colunas legadas). Desde 29/09/2026 é também a fonte
 * do seletor GLOBAL do cabeçalho (`CompanyFilterContext`).
 *
 * Opções = empresas do vínculo OPERACIONAL ativo (`getUserCompanyIds`, o
 * espelho de `extractOperationalCompanyIds` do backend), com o nome vindo de
 * `companies.name` via `GET /companies` (rota só-JWT, sem PoliciesGuard).
 *
 * O gate de "vê todas" é `can_view_all_cost_centers`. Medido no clone antes
 * de reusar: a capability tem EXATAMENTE a mesma população de
 * `can_view_all_processes`, que é o `unrestricted` de `resolveProcessScope`
 * no backend — 31 contas (Desenvolvedor + Presidente), 0 conta com uma e sem
 * a outra. O tipo `agente` perdeu as duas (20260926100000/110000), então ele
 * NÃO passa a ver todas as empresas: cai nas empresas do próprio vínculo,
 * como o backend já o trata (`internal_cc`).
 *
 * Sem can_view_core o escopo é só a empresa do vínculo: o seletor seria um
 * controle sem efeito.
 */
export function useAvailableCompanies() {
  const { user, hasCap } = useAuth()
  const semCore = !hasCap('can_view_core')
  const isDeveloperLike = hasCap('can_view_all_cost_centers')

  const userCompanyIds = useMemo(
    () => new Set(getUserCompanyIds(user)),
    [user]
  )

  const { data: all, isLoading } = useQuery(
    ['companies', 'selectable'],
    () => companiesService.getAll(),
    { enabled: !semCore && (isDeveloperLike || userCompanyIds.size > 1) }
  )

  const options = useMemo<CompanyOption[]>(() => {
    if (semCore || !all) return []
    const source = isDeveloperLike
      ? all
      : all.filter((c) => userCompanyIds.has(Number(c.id)))
    return source
      .map((c) => ({ id: Number(c.id), name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [all, isDeveloperLike, userCompanyIds, semCore])

  return { options, isLoading, canFilter: isDeveloperLike }
}
